import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArrowRight, Home, Search } from "lucide-react";

/**
 * 404 fallback page. Two SEO-critical behaviors:
 *   1. Renders a noindex meta tag so Googlebot never adds unknown routes
 *      to the index (client-side routing means every 404 technically
 *      returns HTTP 200 from nginx, which would otherwise create duplicate
 *      "soft 404" entries).
 *   2. Offers a set of helpful internal links so a real human landing here
 *      by mistake can still find what they wanted — keeps bounce rate low.
 */
export default function NotFound() {
  useEffect(() => {
    // Log for analytics so we can see which URLs are 404'ing and fix
    // broken internal links or create missing pages.
    if (typeof window !== "undefined") {
      console.warn("[404] Path not found:", window.location.pathname);
    }
  }, []);

  return (
    <>
      <SEO
        title="Página não encontrada"
        description="A página solicitada não existe na Biz Certificadora Digital. Veja nossos certificados digitais ICP-Brasil e landings principais."
        noindex
      />
      <Header />
      <main className="min-h-[70vh] py-16 md:py-24">
        <div className="container max-w-3xl">
          <div className="text-center space-y-4">
            <p className="text-sm uppercase tracking-[0.24em] text-muted-foreground">Erro 404</p>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Página não encontrada
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              A rota que você procurou não existe ou foi movida. Use os atalhos abaixo para encontrar o que precisa.
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-4">
              <Button size="lg" asChild>
                <Link to="/">
                  <Home className="h-4 w-4 mr-2" /> Ir para o início
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/qual-certificado">
                  <Search className="h-4 w-4 mr-2" /> Qual certificado preciso?
                </Link>
              </Button>
            </div>
          </div>

          {/* Helpful internal links grid */}
          <div className="mt-16">
            <h2 className="text-xl font-bold mb-6 text-center">Páginas mais acessadas</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { href: "/certificado-pf", title: "Certificado Digital PF (e-CPF)" },
                { href: "/certificado-pj", title: "Certificado Digital PJ (e-CNPJ)" },
                { href: "/certificado-mei", title: "Certificado Digital MEI" },
                { href: "/certificado-digital", title: "Guia completo ICP-Brasil" },
                { href: "/certificado-digital/a1-ou-a3", title: "A1 ou A3: qual escolher" },
                { href: "/renovar-certificado-digital", title: "Renovar certificado" },
                { href: "/certificado-digital/nfe", title: "Certificado para NF-e" },
                { href: "/certificado-digital/esocial", title: "Certificado para eSocial" },
                { href: "/certificado-digital/migrar-serasa", title: "Migrar da Serasa Experian" },
                { href: "/blog", title: "Blog e artigos" },
                { href: "/produtos", title: "Todos os produtos" },
                { href: "/contato-e-suporte", title: "Contato e suporte" },
              ].map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  className="rounded-xl border bg-card p-4 flex items-center justify-between gap-3 hover:border-primary/40 transition"
                >
                  <span className="text-sm font-semibold">{l.title}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
